import { Injectable } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType } from '@azure/msal-browser';
import { Observable, catchError, from, of, switchMap, tap } from 'rxjs';
import { ShipperService } from '../services/shipper.service';
import { UserService } from '../services/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private shipperService: ShipperService,
    private userService: UserService
  ) {
    this.listenForTokenRefresh();
  }

  private listenForTokenRefresh(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        tap((event: EventMessage) => {
          if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
            console.log('Access token refreshed successfully.');
            this.updateLastLoginDate();
          }
        })
      )
      .subscribe();
  }

  public updateLastLoginDate(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount || !activeAccount.idTokenClaims?.oid) {
      console.error('No active account or Object ID found.');
      return;
    }

    const objectId = activeAccount.idTokenClaims.oid;

    this.userService.getUserProfile(objectId).subscribe(
      (profile) => {
        console.log('Profile:', profile);

        if (!profile) {
          console.error('User profile not found.');
          return;
        }

        const userProfile = {
          carrierId: profile.CarrierId,
          objectId: profile.ObjectId,
          principalName: profile.PrincipalName,
          firstName: profile.FirstName,
          lastName: profile.LastName,
          email: profile.Email,
          userType: profile.UserType,
          lastModifiedBy: profile.ObjectId,
          lastLoginDate: new Date().toISOString().split('.')[0]
        };

        if (userProfile.userType.toLowerCase().includes('carrier')) {
          this.userService.upsertCarrierUser(userProfile).subscribe(
            () => console.log('Carrier user LastLogin updated successfully'),
            (error) => console.error('Error updating carrier user:', error)
          );
        } else if (userProfile.userType.toLowerCase().includes('shipper')) {
          this.shipperService.upsertShipperUser(userProfile).subscribe(
            () => console.log('Shipper user LastLogin updated successfully'),
            (error) => console.error('Error updating shipper user:', error)
          );
        } else {
          console.error('Unknown UserType:', userProfile.userType);
        }
      },
      (error) => {
        console.error('Error fetching user profile:', error);
      }
    );
  }

  getB2cObjectId(): Observable<string | null> {
    return from(this.waitForAccountData()).pipe(
      switchMap(() => {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length === 0) {
          console.error('No accounts are signed in.');
          return of(null);
        }

        const activeAccount =
          this.msalService.instance.getActiveAccount() || accounts[0];
        this.msalService.instance.setActiveAccount(activeAccount);

        const oid = activeAccount.idTokenClaims?.oid;
        if (oid) {
          localStorage.setItem('objectId', oid);
          return of(oid);
        } else {
          console.error('No Object ID found in token claims.');
          return of(null);
        }
      }),
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  async waitForAccountData(retryCount = 0): Promise<void> {
    const maxRetries = 10;
    const delay = 1000;

    return new Promise((resolve, reject) => {
      const checkAccounts = () => {
        if (
          this.msalService.instance.getAllAccounts().length > 0 ||
          retryCount >= maxRetries
        ) {
          resolve();
        } else {
          setTimeout(() => {
            this.waitForAccountData(retryCount + 1)
              .then(resolve)
              .catch(reject);
          }, delay);
        }
      };

      checkAccounts();
    });
  }
}
